
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { formatCertificateNumber } from '@/utils/certificateUtils';
import { format } from 'date-fns';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CertificateAuditTrail } from './CertificateAuditTrail';
import { CertificateStatusBadge } from './CertificateStatusBadge';
import { CertificatePDFGenerator } from './CertificatePDFGenerator';
import { 
  AlertCircle, 
  Calendar, 
  FileText,
  Loader2, 
  User
} from 'lucide-react';

interface CertificateDetailViewProps {
  certificateNumber: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CertificateDetailView({
  certificateNumber,
  open,
  onOpenChange
}: CertificateDetailViewProps) {
  const [activeTab, setActiveTab] = useState('overview');
  
  const { data: certificateData, isLoading, error } = useQuery({
    queryKey: ['certificateDetail', certificateNumber],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('certificate_number_inventory')
        .select(`
          *,
          certificates:used_by (
            id,
            user_id,
            issued_at,
            unique_code,
            is_revoked,
            revocation_reason,
            profiles:user_id (
              full_name,
              email
            )
          )
        `)
        .eq('certificate_number', certificateNumber)
        .single();
        
      if (error) throw error;
      return data;
    },
    enabled: open
  });
  
  // Create an audit record when viewing the certificate details
  React.useEffect(() => {
    if (open && certificateNumber) {
      const createViewAudit = async () => {
        const { error } = await supabase
          .from('certificate_verification_history')
          .insert({
            certificate_number: certificateNumber,
            verification_status: 'viewed',
            notes: 'Certificate detail viewed',
            inventory_id: certificateData?.id
          });
          
        if (error) {
          console.error('Error creating view audit:', error);
        }
      };
      
      createViewAudit();
    }
  }, [open, certificateNumber, certificateData?.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Certificate Details
          </DialogTitle>
          <DialogDescription>
            Comprehensive information about certificate {formatCertificateNumber(certificateNumber)}
          </DialogDescription>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : error ? (
          <div className="p-4 bg-red-50 text-red-700 rounded-md flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Error loading certificate details
          </div>
        ) : certificateData ? (
          <Tabs defaultValue="overview" onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid grid-cols-4 w-full mb-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="history">Audit History</TabsTrigger>
              <TabsTrigger value="reports">Reports</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview">
              <div className="grid md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Certificate Information</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <dl className="grid grid-cols-2 gap-2">
                      <dt className="font-medium text-muted-foreground">Certificate Number</dt>
                      <dd>{formatCertificateNumber(certificateData.certificate_number)}</dd>
                      
                      <dt className="font-medium text-muted-foreground">State</dt>
                      <dd>{certificateData.state}</dd>
                      
                      <dt className="font-medium text-muted-foreground">Course Type</dt>
                      <dd>{certificateData.course_type}</dd>
                      
                      <dt className="font-medium text-muted-foreground">Status</dt>
                      <dd>
                        {certificateData.is_used ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700">Used</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-green-50 text-green-700">Available</Badge>
                        )}
                      </dd>
                      
                      <dt className="font-medium text-muted-foreground">Created</dt>
                      <dd>{format(new Date(certificateData.created_at), 'MMM d, yyyy')}</dd>
                      
                      {certificateData.is_used && (
                        <>
                          <dt className="font-medium text-muted-foreground">Assigned</dt>
                          <dd>
                            {certificateData.assigned_at 
                              ? format(new Date(certificateData.assigned_at), 'MMM d, yyyy')
                              : '—'}
                          </dd>
                          
                          <dt className="font-medium text-muted-foreground">Reporting Status</dt>
                          <dd><CertificateStatusBadge type="reporting" item={certificateData} /></dd>
                          
                          <dt className="font-medium text-muted-foreground">Verification Status</dt>
                          <dd><CertificateStatusBadge type="verification" item={certificateData} /></dd>
                        </>
                      )}
                    </dl>
                  </CardContent>
                </Card>
                
                {certificateData.is_used && certificateData.certificates && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Student Information</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm">
                      {(certificateData.certificates as any)?.profiles ? (
                        <dl className="grid grid-cols-2 gap-2">
                          <dt className="font-medium text-muted-foreground">Name</dt>
                          <dd className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            {(certificateData.certificates as any).profiles.full_name}
                          </dd>
                          
                          <dt className="font-medium text-muted-foreground">Email</dt>
                          <dd>{(certificateData.certificates as any).profiles.email}</dd>
                          
                          <dt className="font-medium text-muted-foreground">Issue Date</dt>
                          <dd className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            {format(new Date(certificateData.certificates.issued_at), 'MMM d, yyyy')}
                          </dd>
                          
                          <dt className="font-medium text-muted-foreground">Unique Code</dt>
                          <dd>{certificateData.certificates.unique_code}</dd>
                          
                          <dt className="font-medium text-muted-foreground">Revocation Status</dt>
                          <dd>
                            {certificateData.certificates.is_revoked ? (
                              <Badge variant="destructive">Revoked</Badge>
                            ) : (
                              <Badge variant="outline" className="bg-green-50 text-green-700">Active</Badge>
                            )}
                          </dd>
                          
                          {certificateData.certificates.is_revoked && (
                            <>
                              <dt className="font-medium text-muted-foreground">Reason</dt>
                              <dd>{certificateData.certificates.revocation_reason || 'Not specified'}</dd>
                            </>
                          )}
                        </dl>
                      ) : (
                        <p className="text-muted-foreground">Student information not available</p>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="history">
              <CertificateAuditTrail 
                certificateNumber={certificateNumber} 
                certificateId={certificateData.id}
                showFilters={false}
              />
            </TabsContent>
            
            <TabsContent value="reports">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Reporting History</CardTitle>
                  <CardDescription>
                    Record of report submissions that include this certificate
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {certificateData.report_id ? (
                    <p>This certificate was reported in report #{certificateData.report_id}</p>
                  ) : (
                    <p className="text-muted-foreground">This certificate has not been included in any reports</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="documents">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Certificate Documents</CardTitle>
                  <CardDescription>
                    Generate and manage certificate documents
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {certificateData.is_used && certificateData.certificates ? (
                    <div className="space-y-4">
                      <div className="border-b pb-4">
                        <h4 className="font-medium mb-2">Certificate PDF</h4>
                        <CertificatePDFGenerator 
                          certificate={{
                            id: certificateData.certificates.id,
                            certificate_number: certificateData.certificate_number,
                            issued_at: certificateData.certificates.issued_at,
                            unique_code: certificateData.certificates.unique_code,
                            user_id: certificateData.certificates.user_id,
                            profiles: (certificateData.certificates as any).profiles,
                          }} 
                        />
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground">
                      No documents available. Certificate must be issued to generate documents.
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        ) : (
          <div className="p-4 bg-yellow-50 text-yellow-700 rounded-md flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Certificate not found
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
